import * as RadDialog from '@radix-ui/react-dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { useAction, useAtom } from '@reatom/npm-react'
import React from 'react'
import styled, { keyframes } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { reatomSearchPair } from '@root/features/services/search-pair'
import { useTranslation } from '@root/shared/localization'

import { searchModalOpenAtom } from '../atoms'

import { SearchModalPairsResults } from './search-modal-pairs-results'
import { SearchModalQuery } from './search-modal-query'
import { SearchRecent } from './search-recent'

import type { SearchPairModel } from '@root/features/services/search-pair'

type SearchModalProps = {
  model?: SearchPairModel
}

export const SearchModal: React.FC<SearchModalProps> = ({
  model: defaultModel,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useAtom(searchModalOpenAtom)

  const pairsModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => defaultModel ?? reatomSearchPair(),
    [defaultModel],
  )

  const [pairsSearchQuery] = useAtom(pairsModel.searchQueryAtom)
  const setPairsSearchQuery = useAction(pairsModel.setQueryAction)

  const setSearchQuery = (newSearchQuery: string) => {
    setPairsSearchQuery(newSearchQuery)
  }

  const handlePairSelected = () => {
    setOpen(false)
  }

  return (
    <Root open={open} onOpenChange={setOpen}>
      <RadDialog.Portal>
        <Overlay />
        <Content aria-describedby={t('common.search')}>
          <VisuallyHidden.Root>
            <RadDialog.Description>{t('common.search')}</RadDialog.Description>
            <RadDialog.DialogTitle>{t('common.search')}</RadDialog.DialogTitle>
          </VisuallyHidden.Root>
          <SearchModalQuery
            query={pairsSearchQuery}
            onQueryChange={setSearchQuery}
            onOpenChange={setOpen}
          />

          <PairsInnerContent>
            <SearchRecent onPairSelected={handlePairSelected} />
            <SearchModalPairsResults
              model={pairsModel}
              onPairSelected={handlePairSelected}
              onClose={() => setOpen(false)}
            />
          </PairsInnerContent>
        </Content>
      </RadDialog.Portal>
    </Root>
  )
}

const Root = styled(RadDialog.Root)``

const overlayShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`

const Overlay = styled(RadDialog.Overlay)`
  background: var(--appearanceA-5);
  backdrop-filter: blur(8px);

  position: fixed;
  inset: 0;
  animation: ${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
`

const Content = styled(RadDialog.Content)`
  box-shadow: inset 0 0 0 1px var(--backgroundA-3);
  background: var(--background-1);

  backdrop-filter: blur(10px);
  border-radius: 8px;
  width: 720px;
  min-height: 320px;
  max-height: 640px;
  height: 80vh;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  // Dialog positioning
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);

  &:focus {
    outline: none;
  }
`

const PairsInnerContent = styled.div`
  display: grid;
  grid-template-rows: [recents] auto [results] minmax(0, 1fr);
  min-height: 0;
  padding-block: 10px 0;
`
